import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { GenericError } from '@auth0/auth0-react';

// Older backends report auth failures only in the error message.
export const STARGATE_UNAUTHORIZED_MESSAGE = '401: Unauthorized';
export const PAPI_UNAUTHORIZED_MESSAGE =
  'Context creation failed: AuthenticationError: User does not have access to organization';

// Auth0 error codes that logging in again resolves.
const AUTH0_REAUTH_ERRORS = [
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'missing_refresh_token',
  'mfa_required',
];

const isAuthStatus = (status: unknown) => status === 401 || status === 403;

// Returns true when logging in again could fix the error, and false for
// network and server problems, which a re-login cannot fix.
export const isAuthError = (error: unknown): boolean => {
  // The token refresh in authLink failed because the Auth0 session is gone.
  // Other codes (timeouts, server_error, rate limits) mean Auth0 could not be
  // reached. A network failure is a plain TypeError, so it never gets here.
  if (error instanceof GenericError) {
    return AUTH0_REAUTH_ERRORS.includes(error.error);
  }

  // A 403 on the request itself comes from in front of the router (e.g. a WAF),
  // which logging in again does not fix.
  if (ServerError.is(error)) {
    return error.statusCode === 401;
  }

  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some(graphQLError => {
      const { code, service, http } = graphQLError.extensions ?? {};
      const message = JSON.stringify(graphQLError);
      return (
        code === 'UNAUTHENTICATED' ||
        // The router answers with HTTP 200 when the auth subgraph rejects the
        // token, and reports the subgraph's status in the error extensions.
        (code === 'SUBREQUEST_HTTP_ERROR' &&
          service === 'auth' &&
          isAuthStatus((http as { status?: number } | undefined)?.status)) ||
        message.includes(STARGATE_UNAUTHORIZED_MESSAGE) ||
        message.includes(PAPI_UNAUTHORIZED_MESSAGE)
      );
    });
  }

  return false;
};
